import { Inject, Injectable } from "@angular/core";
import { AirportEntity, AppMetaData, CheckRideRequestCalendarQueryEntity, CheckRideRequestEntity, CheckrideRequestSchoolCalendarQueryEntity, CheckRideRequestSubStatusAllItems, CheckRideScenarioEntity, CheckRideTypeEntity, FlightSchoolApplicantEntity, FlightSchoolApplicantStatusAllItems, FlightSchoolExaminerEntity, FlightSchoolExaminerWithExaminerDetailsQueryEntity, FlightSchoolOrganizationEntity, PilotExaminerEntity } from "../../../meta-data/app-meta-data.service";
import { DwOrmDataServiceToken, DwOrmDataService, DwMetaDataServiceToken, DwMetaDataService, DwConfigServiceToken, DwConfigService, DwSecurityUserService, DwCacheService, DwQueryFilter, DwQuery, DwQueryHelper, DwFieldType, DwFieldAggregate, DwSecurityTopics } from "@devwareapps/devware-cap";
import { Observable, of } from "rxjs";
import { AdminManagerService } from "../../shared/services/admin-manager.service";
import { map, mergeMap, tap } from "rxjs/operators";
import { AirportMetar } from "../../examiner/models/airport-metar.model";
import { MetarRepositoryService } from "../../shared/services/metar-repository-service";
import { ValidateSchoolCheckridesResult } from "../models/validate-school-checkrides-result.model";
import { HttpClient } from "@angular/common/http";
import { ValidateSchoolCheckridesRequest } from "../models/validate-school-checkrides-request.model";
import { SchoolFeatures } from "../models/school-features.model";
import { PilexosUserTypeIds } from "../../shared/models/pilexos-user-type.enum";
import { ConvertCheckInstructorResult } from "../models/convert-check-instructor-result.model";
import { ConvertCheckInstructorRequest } from "../models/convert-check-instructor-request.model";
import { FlightSchoolExaminerTrainingStatus } from "../models/flight-school-examiner-training-status.model";


@Injectable({
    providedIn: 'root'
})
export class SchoolRepositoryService {



    static flightSchoolOrganization: FlightSchoolOrganizationEntity;
    static lastApplicant: FlightSchoolApplicantEntity;

    apiRoot: string;
    constructor(
        @Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private adminManagerSerice: AdminManagerService,
        private metarRepositoryService: MetarRepositoryService,
        private http: HttpClient,
    ) {
        this.apiRoot = configService.coreConfig.apiRoot;
    }

    storeCurrentPublicFlightSchool(flightSchoolOrganization: FlightSchoolOrganizationEntity) {
        SchoolRepositoryService.flightSchoolOrganization = flightSchoolOrganization;
    }

    getCurrentPublicFlightSchool(): FlightSchoolOrganizationEntity {
        return SchoolRepositoryService.flightSchoolOrganization;
    }

    storeLastApplicant(applicant: FlightSchoolApplicantEntity) {
        SchoolRepositoryService.lastApplicant = applicant;
    }

    getLastApplicant(): FlightSchoolApplicantEntity {
        return SchoolRepositoryService.lastApplicant;
    }

    saveCheckrides(checkridesToSave: CheckRideRequestEntity[]): Observable<CheckRideRequestEntity[]> {
        return this.dwOrmDataService.saveEntityCollection(checkridesToSave);
    }

    getSchoolExaminers(): Observable<FlightSchoolExaminerWithExaminerDetailsQueryEntity[]> {
        const cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolExaminer.ItemDetail.itemName));
        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        const key = `school-examiners_${pilexosUser.flightSchoolOrganizationId}`;

        return this.dwCacheService.get(key, this.loadSchoolExaminers(pilexosUser.flightSchoolOrganizationId), null, cacheInvalidationTopics);
    }

    private loadSchoolExaminers(flightSchoolOrganizationId: number): Observable<FlightSchoolExaminerWithExaminerDetailsQueryEntity[]> {

        return AppMetaData.Queries.FlightSchoolExaminerWithExaminerDetails.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {


                const pilexosUser = this.adminManagerSerice.getPilexosUser();

                query.addFilterAnd(filters => filters.Equal(field => field.FlightSchoolOrganizationId, flightSchoolOrganizationId));

                return this.dwOrmDataService.executeQuery(query)
            }));
    }


    // Define methods to interact with the school data repository
    // For example, methods to get school details, update school information, etc.

    // getSchoolDetails(schoolId: number): Observable<SchoolDetails> {
    //     // Logic to fetch school details by ID
    //     return of({} as SchoolDetails); // Placeholder for actual implementation
    // }

    // updateSchoolDetails(schoolDetails: SchoolDetails): Observable<boolean> {
    //     // Logic to update school details
    //     return of(true); // Placeholder for actual implementation
    // } 

    getFlightSchoolByProfileKey(profileKey: string): Observable<FlightSchoolOrganizationEntity> {
        const query = AppMetaData.FlightSchoolOrganization.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.PublicPortalSlug, profileKey));

        query.setFieldSettings((settings) => {
            settings.LoadAllLookupDisplayFields = true;
        });

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getSchoolCheckRides(startDate: string, endDate: string): Observable<CheckrideRequestSchoolCalendarQueryEntity[]> {
        return AppMetaData.Queries.CheckrideRequestSchoolCalendar.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {
                const pilexosUser = this.adminManagerSerice.getPilexosUser();

                query.addFilterAnd(filters => filters.Equal(field => AppMetaData.CheckRideRequest.Attributes.FlightSchoolOrganizationId, pilexosUser.flightSchoolOrganizationId));

                // We need to use both the Schedule Date Time and the Proposed Date Time (when the checkride is in proposed status)
                query.addFilterAnd(f =>
                    f.GroupOr(f =>
                        f.GroupAnd(
                            f => this.setExactTimeCompareValue(f.GreaterThanEqual(f2 => f2.ScheduledDateTime, startDate), false),
                            f => this.setExactTimeCompareValue(f.LessThanEqual(f2 => f2.ScheduledDateTime, endDate), false)
                        ), f =>
                        f.GroupAnd(
                            f => this.setExactTimeCompareValue(f.GreaterThanEqual(f2 => f2.ProposedDateTime, startDate), false),
                            f => this.setExactTimeCompareValue(f.LessThanEqual(f2 => f2.ProposedDateTime, endDate), false),
                            f => f.Equal(a => a.CheckRideRequestSubStatusId, CheckRideRequestSubStatusAllItems.NewDateProposedByExaminer),
                        )
                    )
                );

                // This is added in the query so it's easy to maintain
                //query.addFilterAnd(fb => fb.InList(f => f.CheckRideRequestSubStatusId, [CheckRideRequestSubStatusAllItems.PendingApproval, CheckRideRequestSubStatusAllItems.PendingOutcome, CheckRideRequestSubStatusAllItems.NewDateProposedByExaminer, CheckRideRequestSubStatusAllItems.PendingPayment]));

                return this.dwOrmDataService.executeQuery<CheckRideRequestCalendarQueryEntity>(query);
            }));
    }




    private setExactTimeCompareValue(filter: DwQueryFilter, value: boolean): DwQueryFilter {
        filter.ExactDateTimeCompare = value;

        return filter;
    }


    validateSchoolCheckrides(request: ValidateSchoolCheckridesRequest): Observable<ValidateSchoolCheckridesResult> {
        const url = `${this.apiRoot}/pilexos/booking/validate-school-checkrides`;

        return this.http.post<ValidateSchoolCheckridesResult>(url, request);
    }

    getSchoolFeatures(): Observable<SchoolFeatures> {

        const cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolOrganization.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwUser.ItemDetail.itemName));

        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        const validUserTypes = [PilexosUserTypeIds.flightSchool, PilexosUserTypeIds.checkInstructor, PilexosUserTypeIds.flightInstructor];

        if (!pilexosUser?.user?.UserId || !validUserTypes.includes(pilexosUser?.UserTypeId)) {
            return of({} as SchoolFeatures);
        }

        return this.dwCacheService.get('school-features', this.loadSchoolFeatures(), null, cacheInvalidationTopics);
    }



    private loadSchoolFeatures(): Observable<SchoolFeatures> {
        const url = `${this.apiRoot}/pilexos/schools/school-details`;

        return this.http.get<SchoolFeatures>(url)
            .pipe(map(features => {

                if (features == null) {
                    console.warn('SchoolRepositoryService.loadSchoolFeatures - No features returned from API');
                }
                return features;

            }));
    }

    getFlightSchoolExaminerTrainingStatus(): Observable<FlightSchoolExaminerTrainingStatus> {
        const cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolOrganization.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolExaminer.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.TrainingRecord.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.TrainingType.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.DwUser.ItemDetail.itemName));

        const pilexosUser = this.adminManagerSerice.getPilexosUser();
        const validUserTypes = [PilexosUserTypeIds.flightInstructor, PilexosUserTypeIds.checkInstructor];

        if (!pilexosUser?.user?.UserId || !validUserTypes.includes(pilexosUser?.UserTypeId)) {
            return of({} as FlightSchoolExaminerTrainingStatus);
        }

        return this.dwCacheService.get('flight-school-examiner-training-status', this.loadFlightSchoolExaminerTrainingStatus(), null, cacheInvalidationTopics);
    }

    private loadFlightSchoolExaminerTrainingStatus(): Observable<FlightSchoolExaminerTrainingStatus> {
        const url = `${this.apiRoot}/pilexos/schools/flight-school-examiner-training-status`;

        return this.http.get<FlightSchoolExaminerTrainingStatus>(url)
            .pipe(map(result => result || {} as FlightSchoolExaminerTrainingStatus));
    }

    getCurrentFlightSchoolStats(includeCheckrideDetailsOnly: boolean, includeHomeCardDetails: boolean = false, skipCheckrides: boolean = false) {
        const query = AppMetaData.FlightSchoolOrganization.CreateQueryBuilder();


        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        query.addFilterAnd(filters => filters.Equal(field => field.FlightSchoolOrganizationId, pilexosUser.flightSchoolOrganizationId));

        query.query.FieldSettings.IncludeRootItemFields = true;
        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addPrefetch(r => r.AddressByHeadquatersAddressId);
        query.addPrefetch(r => r.FlightSchoolTrainingProgram);

        if (!includeCheckrideDetailsOnly) {
            var fsbSubQuery = AppMetaData.FlightSchoolBranch.CreateQueryBuilder();
            fsbSubQuery.setupPaging(1, 1, false, 1);

            query.addPrefetch(r => r.FlightSchoolBranch, AppMetaData.FlightSchoolBranch.CreateQueryBuilder().setupPaging(1, 1, false, 1).query);

            const applicantQuery = AppMetaData.FlightSchoolApplicant.CreateQueryBuilder().setupPaging(1, 1, false, 1);
            applicantQuery.addFilterAnd(f => f.NotInList(a => a.FlightSchoolApplicantStatusId, [FlightSchoolApplicantStatusAllItems.Inactive, FlightSchoolApplicantStatusAllItems.Deleted]));

            query.addPrefetch(r => r.FlightSchoolApplicant, applicantQuery.query);
        }

        // Build checkride query
        const checkRideQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        checkRideQuery.query.FieldSettings.IncludeRootItemFields = false;

        checkRideQuery.addFields(f => [f.ScheduledPilotExaminerId, f.FlightSchoolOrganizationId, f.CheckRideWorkflowId, f.CheckRideOwnershipTypeId, f.CheckRideRequestSubStatusId, f.StudentApplicantId, f.ScheduledDateTime]);

        if (includeHomeCardDetails) {
            checkRideQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;
            checkRideQuery.addFields(f => [
                f.CheckRideRequestId, f.StudentApplicantNameManual, f.ScheduledTimeMinutes,
                f.CheckRideTypeId, f.CheckRideTestTypeId, f.CheckRideGroupId, f.ScheduledAirportId,
                f.FlightSchoolId, f.FlightSchoolName, f.FlightSchoolNameOther,
                f.AirplaneMakeModel, f.AuthorityApplicationNumber, f.ScheduledCheckRideCost,
                f.PilexosBookingCost, f.PilexosBookingCostPaymentPartyId, f.PilexosBookingPaymentAdded
            ]);

            const applicantNameQuery = AppMetaData.RoCheckrideApplicantFinalName.CreateQueryBuilder();
            applicantNameQuery.query.FieldSettings.IncludeRootItemFields = false;
            applicantNameQuery.addFields(f => [f.ApplicantFinalName, f.FlightSchoolFinalName]);
            checkRideQuery.addPrefetch(r => r.RoCheckrideApplicantFinalName, applicantNameQuery.query);

            const planOfActionQuery = AppMetaData.PlanOfAction.CreateQueryBuilder();
            planOfActionQuery.query.FieldSettings.IncludeRootItemFields = false;
            planOfActionQuery.addFields(f => [f.PlanOfActionId]);
            checkRideQuery.addPrefetch(r => r.PlanOfAction, planOfActionQuery.query);

            const writtenResultQuery = AppMetaData.ApplicantAcsWrittenResult.CreateQueryBuilder();
            writtenResultQuery.query.FieldSettings.IncludeRootItemFields = false;
            writtenResultQuery.addFields(f => [f.AcsTestId]);
            checkRideQuery.addPrefetch(r => r.ApplicantAcsWrittenResult, writtenResultQuery.query);
        }

        // We want to limit to the school, date range and certain statuses
        checkRideQuery.addFilterAnd(filters => filters.Equal(a => a.CheckRideOwnershipTypeId, 2));

        checkRideQuery.addFilterOr(filters => filters.GroupOr(
            fb => fb.GroupAnd(
                fb => fb.DateFilters.GreaterThanDays(f => f.ScheduledDateTime, -1),
                fb => fb.DateFilters.LessThanEqualDays(f => f.ScheduledDateTime, 14),
            ),
            fb => fb.InList(f => f.CheckRideRequestSubStatusId, [CheckRideRequestSubStatusAllItems.PendingApproval, CheckRideRequestSubStatusAllItems.PendingApprovalPayment, CheckRideRequestSubStatusAllItems.PendingOutcome, CheckRideRequestSubStatusAllItems.NewDateProposedByExaminer, CheckRideRequestSubStatusAllItems.PendingPayment, CheckRideRequestSubStatusAllItems.PendingReschedule, CheckRideRequestSubStatusAllItems.Cancelled]),
        ));

        if (!skipCheckrides) {
            query.addPrefetch(r => r.CheckRideRequest, checkRideQuery.query);
        }

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getSchoolRecentSubmittedApplicantStats(): Observable<number> {
        const query = AppMetaData.FlightSchoolApplicant.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.PublicPortalSubmission, true));
        query.addFilterAnd(f => f.DateFilters.GreaterThanEqualDays(a => a.CreatedDateTime, -7));
        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        query.addFilterAnd(f => f.NotInList(a => a.FlightSchoolApplicantStatusId, [FlightSchoolApplicantStatusAllItems.Inactive, FlightSchoolApplicantStatusAllItems.Deleted]));

        query.addFilterAnd(filters => filters.Equal(field => field.FlightSchoolOrganizationId, pilexosUser.flightSchoolOrganizationId));

        const qh = new DwQueryHelper();

        query.query.AddField({
            FieldType: DwFieldType.Aggregate,
            AggregateFunction: DwFieldAggregate.Count,
            FieldAlias: 'Count'
        });

        query.query.FieldSettings.AggregateQuery = true;

        return this.dwOrmDataService.executeQuerySingle(query)
            .pipe(map(result => {
                return (result as any).Count || 0;
            }));
    }

    // getApplicantRebookCheckrideId(FlightSchoolApplicantId: number):Observable<number> {
    //     const query = AppMetaData.CheckRideRequest.CreateQueryBuilder();  

    //     const user = this.adminManagerSerice.getPilexosUser();

    //     query.addFilterAnd(f => f.Equal(a => a.FlightSchoolOrganizationId, user.flightSchoolOrganizationId));
    //     query.addFilterAnd(f => f.Equal(a => a.FlightSchoolApplicantId, FlightSchoolApplicantId));
    //     query.addFilterAnd()

    //     query.addFilterAnd(f => f.Equal(a => a.CheckRideRequestId, checkRideRequestId));
    // }

    getCheckrideRequest(checkRideRequestId: number): Observable<CheckRideRequestEntity> {
        const query = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.CheckRideRequestId, checkRideRequestId));

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addPrefetch(r => r.ApplicantAcsWrittenResult, AppMetaData.ApplicantAcsWrittenResult.CreateQueryBuilder().addPrefetch(r => r.ApplicantAcsWrittenResultMissedCode).query);

        const poaQuery = AppMetaData.PlanOfAction.CreateQueryBuilder();
        poaQuery.query.FieldSettings.IncludeRootItemFields = false;

        poaQuery.addFields(f => [f.PlanOfActionId, f.CheckRideRequestId, f.PlanOfActionStatusId]);

        query.addPrefetch(r => r.PlanOfAction, poaQuery.query);

        return this.dwOrmDataService.executeQuerySingle(query);

    }

    mapStudentApplicants(applicants: FlightSchoolApplicantEntity[]): { publicPortalSubmission: boolean, count: number }[] {
        const finalResult = applicants.map(r => {
            return {
                publicPortalSubmission: r.PublicPortalSubmission,
                count: (r as any).Count
            }
        });

        console.log('Recent Submissions:', finalResult);
        return finalResult;
    }

    getPilexosExaminerDetails(examinerId: number[]): Observable<PilotExaminerEntity[]> {
        const query = AppMetaData.PilotExaminer.CreateQueryBuilder();

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;
        query.addFilterAnd(f => f.InList(a => a.PilotExaminerId, examinerId));

        query.addPrefetch(r => r.CheckRideScenario, AppMetaData.CheckRideScenario.CreateQueryBuilder().addPrefetch(r => r.CheckRideScenarioTypeMap).query);

        return this.dwOrmDataService.executeQuery<PilotExaminerEntity>(query);
    }

    getFlightSchoolApplicant(applicantId: number, includeCheckrides: boolean, includeBranches: boolean = false): Observable<FlightSchoolApplicantEntity> {
        const query = AppMetaData.FlightSchoolApplicant.CreateQueryBuilder();

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolApplicantId, applicantId));

        if (includeCheckrides) {
            const checkrideSubQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();
            checkrideSubQuery.query.FieldSettings.LoadAllLookupDisplayFields = true;

            checkrideSubQuery.addFilterAnd(f => f.NotInList(a => a.CheckRideRequestSubStatusId, [CheckRideRequestSubStatusAllItems.Cancelled, CheckRideRequestSubStatusAllItems.Completed, CheckRideRequestSubStatusAllItems.Deleted]));

            // Only get future checkrides
            checkrideSubQuery.addFilterAnd(f => f.DateFilters.GreaterThanDays(a => a.ScheduledDateTime, 0));
            checkrideSubQuery.addOrderByDesc(a => a.ScheduledDateTime);

            query.addPrefetch(r => r.CheckRideRequest, checkrideSubQuery.query);
        }

        if (includeBranches) {
            const subQuery = AppMetaData.FlightSchoolApplicantBranch.CreateQueryBuilder();
            subQuery.addPrefetch(r => r.FlightSchoolBranch);

            query.addPrefetch(r => r.FlightSchoolBranch);
        }

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getCurrentFlightSchoolApplicants(statuses: number[] = [FlightSchoolApplicantStatusAllItems.AwaitingCheckride, FlightSchoolApplicantStatusAllItems.AwaitingRebook, FlightSchoolApplicantStatusAllItems.CheckrideScheduled]): Observable<FlightSchoolApplicantEntity[]> {
        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        const cacheInvalidationTopics = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.CheckRideRequest.ItemDetail.itemName));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.FlightSchoolApplicant.ItemDetail.itemName));

        const key = `flight-school-applicants-${pilexosUser.flightSchoolOrganizationId}-${statuses.join(',')}`;

        return this.dwCacheService.get(key, this.getFlightSchoolApplicants(pilexosUser.flightSchoolOrganizationId, statuses));
    }

    private getFlightSchoolApplicants(flightSchoolOrganizationId: number, statuses: number[] = [FlightSchoolApplicantStatusAllItems.AwaitingCheckride]): Observable<FlightSchoolApplicantEntity[]> {
        const query = AppMetaData.FlightSchoolApplicant.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolOrganizationId, flightSchoolOrganizationId));

        if (statuses?.length > 0) {
            query.addFilterAnd(f => f.InList(a => a.FlightSchoolApplicantStatusId, statuses));
        } else {
            query.addFilterAnd(f => f.NotInList(a => a.FlightSchoolApplicantStatusId, [FlightSchoolApplicantStatusAllItems.Inactive, FlightSchoolApplicantStatusAllItems.Deleted]));
        }

        const subQuery = AppMetaData.FlightSchoolApplicantBranch.CreateQueryBuilder();
        subQuery.addPrefetch(r => r.FlightSchoolBranch);

        query.addPrefetch(r => r.FlightSchoolBranch);

        query.addPrefetch(r => r.ApplicantAcsWrittenResult, AppMetaData.ApplicantAcsWrittenResult.CreateQueryBuilder().addPrefetch(r => r.ApplicantAcsWrittenResultMissedCode).query);

        query.query.FieldSettings.LoadAllLookupDisplayFields = true;

        return this.dwOrmDataService.executeQuery(query)
    }

    getCurrentFlightSchoolMetarAirports(): Observable<AirportEntity[]> {
        return AppMetaData.Queries.FlightSchoolAirports.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {

                const pilexosUser = this.adminManagerSerice.getPilexosUser();

                query.addFilterAnd(f => f.Equal(a => a.MainFlightSchoolBranchFlightSchoolOrganizationId, pilexosUser.flightSchoolOrganizationId));
                query.addFilterAnd(f => f.Equal(a => a.MainFlightSchoolBranchShowMetar, true));

                return this.dwOrmDataService.executeQuery(query);
            }));
    }

    public getCurrentFlightSchoolMetarData(maxAirports: number = 5): Observable<AirportMetar[]> {

        return this.getCurrentFlightSchoolMetarAirports()
            .pipe(mergeMap(airports => {
                const obs: Observable<AirportMetar>[] = [];

                if (airports.length == 0) {
                    return of([])
                }

                if (airports.length > maxAirports) {
                    airports.length = maxAirports;
                }


                return this.metarRepositoryService.getAirportMetars(airports);
            }));
    }

    getRebookCheckrideRequest(rebookCheckrideId: number): Observable<CheckRideRequestEntity> {
        const checkRideQuery = AppMetaData.CheckRideRequest.CreateQueryBuilder();

        const pilexosUser = this.adminManagerSerice.getPilexosUser();

        checkRideQuery.addFilterAnd(filters => filters.Equal(field => field.CheckRideRequestId, rebookCheckrideId));


        checkRideQuery.addFilterAnd(filters => filters.Equal(field => field.FlightSchoolOrganizationId, pilexosUser.flightSchoolOrganizationId));

        // Check the result as well
        checkRideQuery.addPrefetch(r => r.CheckRideResult);
        checkRideQuery.addPrefetch(r => r.CheckRidePayment);

        return this.dwOrmDataService.executeQuerySingle(checkRideQuery);
    }

    convertToCheckInstructor(request: ConvertCheckInstructorRequest): Observable<ConvertCheckInstructorResult> {
        const url = `${this.apiRoot}/pilexos/schools/convert-check-instructor`;

        return this.http.post<ConvertCheckInstructorResult>(url, request);
    }

}
